<template>
	<div class="juniper-dashboard">
		<!-- Page header -->
		<div class="jd-header">
			<div class="jd-header-left">
				<h1 class="jd-title">Dashboard</h1>
				<p class="jd-subtitle">Juniper CMS overview</p>
			</div>
		</div>

		<!-- Stat cards -->
		<div class="jd-stats-grid">
			<div class="jd-stat-card jd-stat-card--compact jd-stat-card--metric jd-stat-card--metric-live">
				<div class="jd-stat-top">
					<div class="jd-stat-chip jd-stat-chip--live">
						<font-awesome-icon :icon="['fas', 'check-circle']" />
					</div>
					<div class="jd-stat-label jd-stat-label--metric">Live</div>
				</div>
				<div class="jd-stat-value jd-stat-value--metric jd-stat-value--live">
					<span v-if="!loadingStats && stats">{{ stats.live_count }}</span>
					<font-awesome-icon v-else :icon="['fas', 'spinner']" class="fa-spin jd-stat-spin" />
				</div>
				<div class="jd-stat-note">Production websites</div>
			</div>

			<div class="jd-stat-card jd-stat-card--compact jd-stat-card--metric jd-stat-card--metric-staging">
				<div class="jd-stat-top">
					<div class="jd-stat-chip jd-stat-chip--staging">
						<font-awesome-icon :icon="['fas', 'clock']" />
					</div>
					<div class="jd-stat-label jd-stat-label--metric">Staging</div>
				</div>
				<div class="jd-stat-value jd-stat-value--metric jd-stat-value--staging">
					<span v-if="!loadingStats && stats">{{ stats.staging_count }}</span>
					<font-awesome-icon v-else :icon="['fas', 'spinner']" class="fa-spin jd-stat-spin" />
				</div>
				<div class="jd-stat-note">Pre-launch environments</div>
			</div>

			<div class="jd-stat-card jd-stat-card--compact jd-stat-card--metric jd-stat-card--metric-unified">
				<div class="jd-stat-top">
					<div class="jd-stat-chip jd-stat-chip--unified">
						<font-awesome-icon :icon="['fas', 'globe']" />
					</div>
					<div class="jd-stat-label jd-stat-label--metric">Unified</div>
				</div>
				<div class="jd-stat-value jd-stat-value--metric jd-stat-value--unified">
					<span v-if="!loadingDashboardUsage">{{ unifiedCount }}</span>
					<font-awesome-icon v-else :icon="['fas', 'spinner']" class="fa-spin jd-stat-spin" />
				</div>
				<div class="jd-stat-note">Using unified dashboard</div>
			</div>

			<div class="jd-stat-card jd-stat-card--compact jd-stat-card--metric jd-stat-card--metric-town-web">
				<div class="jd-stat-top">
					<div class="jd-stat-chip jd-stat-chip--town-web">
						<font-awesome-icon :icon="['fas', 'city']" />
					</div>
					<div class="jd-stat-label jd-stat-label--metric">Town Web</div>
				</div>
				<div class="jd-stat-value jd-stat-value--metric jd-stat-value--town-web">
					<span v-if="!loadingDashboardUsage">{{ townWebCount }}</span>
					<font-awesome-icon v-else :icon="['fas', 'spinner']" class="fa-spin jd-stat-spin" />
				</div>
				<div class="jd-stat-note">Using classic dashboard</div>
			</div>
		</div>

		<div class="jd-bottom-grid">
			<div class="jd-panel">
				<div class="jd-section-title">Quick Actions</div>
				<div class="jd-actions-grid">
					<router-link class="jd-action-card" to="/juniper/clients/new">
						<div class="jd-action-icon jd-action-icon--blue">
							<font-awesome-icon :icon="['fas', 'plus-circle']" />
						</div>
						<div class="jd-action-label">New Client</div>
						<div class="jd-action-desc">Create a new Juniper CMS client</div>
					</router-link>

					<router-link class="jd-action-card" to="/juniper/clients">
						<div class="jd-action-icon jd-action-icon--teal">
							<font-awesome-icon :icon="['fas', 'globe']" />
						</div>
						<div class="jd-action-label">All Clients</div>
						<div class="jd-action-desc">Browse and manage all clients</div>
					</router-link>

					<router-link class="jd-action-card" to="/juniper/themes">
						<div class="jd-action-icon jd-action-icon--purple">
							<font-awesome-icon :icon="['fas', 'palette']" />
						</div>
						<div class="jd-action-label">Themes</div>
						<div class="jd-action-desc">Manage CMS themes and versions</div>
					</router-link>

					<router-link class="jd-action-card" to="/juniper/statistics">
						<div class="jd-action-icon jd-action-icon--rose">
							<font-awesome-icon :icon="['far', 'chart-bar']" />
						</div>
						<div class="jd-action-label">Statistics</div>
						<div class="jd-action-desc">Cross-client insights and reports</div>
					</router-link>

					<router-link
						v-if="userHasOneOfNeededJuniperPermissions(['view_users', 'view_roles', 'view_permissions'])"
						class="jd-action-card"
						to="/juniper/user-management"
					>
						<div class="jd-action-icon jd-action-icon--orange">
							<font-awesome-icon :icon="['fas', 'users-cog']" />
						</div>
						<div class="jd-action-label">User Management</div>
						<div class="jd-action-desc">Roles, permissions and users</div>
					</router-link>
				</div>
			</div>

			<div class="jd-panel jd-latest-panel">
				<div class="jd-section-title">Latest Launched Clients</div>
				<div v-if="loadingLatestLaunched" class="jd-latest-loading">
					<font-awesome-icon :icon="['fas', 'spinner']" class="fa-spin" />
				</div>
				<div v-else-if="latestLaunchedClients.length === 0" class="jd-latest-empty">No launched clients found.</div>
				<div v-else class="jd-latest-list">
					<router-link
						v-for="client in latestLaunchedClients"
						:key="client.id"
						class="jd-latest-item"
						:to="'/juniper/clients/edit/' + client.id"
					>
						<div class="jd-latest-client">
							<div class="jd-latest-logo-wrap">
								<img v-if="client.logo" :src="client.logo" :alt="client.name" class="jd-latest-logo" />
								<div v-else class="jd-latest-logo-fallback">
									<font-awesome-icon :icon="['fas', 'globe']" />
								</div>
							</div>
							<div class="jd-latest-name">{{ client.name }}</div>
						</div>
						<div class="jd-latest-date">{{ formatLaunchedDate(client.launched_at) }}</div>
					</router-link>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import juniperApi from '../../api/juniperApi'
import juniperUserMixin from '@/mixins/juniperUserMixin'

export default {
	name: 'Home',
	data() {
		return {
			loadingStats: false,
			loadingDashboardUsage: false,
			loadingLatestLaunched: false,
			stats: null,
			unifiedCount: 0,
			townWebCount: 0,
			latestLaunchedClients: [],
		}
	},
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']),
	},
	created() {
		this.fetchStats()
		this.fetchDashboardUsage()
		this.fetchLatestLaunchedClients()
	},
	methods: {
		fetchStats() {
			this.loadingStats = true
			juniperApi
				.get('/clients/stats')
				.then(({ data }) => {
					this.stats = data
				})
				.catch(() => {})
				.finally(() => {
					this.loadingStats = false
				})
		},
		fetchDashboardUsage() {
			this.loadingDashboardUsage = true
			Promise.all([juniperApi.get('/clients/stats', { params: { dashboard: 'unified' } }), juniperApi.get('/clients/stats', { params: { dashboard: 'town-web' } })])
				.then(([unifiedRes, townWebRes]) => {
					this.unifiedCount = unifiedRes?.data?.total_count || 0
					this.townWebCount = townWebRes?.data?.total_count || 0
				})
				.catch(() => {
					this.unifiedCount = 0
					this.townWebCount = 0
				})
				.finally(() => {
					this.loadingDashboardUsage = false
				})
		},
		fetchLatestLaunchedClients() {
			this.loadingLatestLaunched = true
			juniperApi
				.get('/clients', { params: { all: true } })
				.then(({ data }) => {
					const clients = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])
					this.latestLaunchedClients = clients
						.filter(client => client?.launched_at)
						.sort((a, b) => new Date(b.launched_at).getTime() - new Date(a.launched_at).getTime())
						.slice(0, 5)
				})
				.catch(() => {
					this.latestLaunchedClients = []
				})
				.finally(() => {
					this.loadingLatestLaunched = false
				})
		},
		formatLaunchedDate(launchedAt) {
			if (!launchedAt) return 'No launch date'
			const date = new Date(launchedAt)
			if (Number.isNaN(date.getTime())) return 'Invalid date'
			return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
		},
	},
	mixins: [juniperUserMixin],
}
</script>

<style scoped>
.juniper-dashboard {
	padding: 28px 32px;
}

/* Header */
.jd-header {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	margin-bottom: 28px;
}

.jd-title {
	font-size: 24px;
	font-weight: 700;
	color: #111827;
	margin: 0 0 4px 0;
}

.jd-subtitle {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
}

/* Stats grid */
.jd-stats-grid {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 18px;
	margin-bottom: 32px;
}

@media (max-width: 1100px) {
	.jd-stats-grid {
		grid-template-columns: repeat(2, 1fr);
	}
}

.jd-stat-card {
	background: #fff;
	border-radius: 12px;
	padding: 20px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.jd-stat-card--status {
	gap: 10px;
}

.jd-stat-card--compact {
	padding: 14px 16px;
	gap: 8px;
}

.jd-stat-card--metric {
	align-items: flex-start;
	justify-content: center;
	min-height: 132px;
	border: 1px solid transparent;
	transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.jd-stat-card--metric:hover {
	transform: translateY(-2px);
	box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
}

.jd-stat-card--metric-live {
	background: linear-gradient(180deg, #f6fff8 0%, #eefcf3 100%);
	border-color: #bbf7d0;
}

.jd-stat-card--metric-staging {
	background: linear-gradient(180deg, #fff9f2 0%, #fff2e2 100%);
	border-color: #fed7aa;
}

.jd-stat-card--metric-unified {
	background: linear-gradient(180deg, #f3fffd 0%, #e9fcf7 100%);
	border-color: #99f6e4;
}

.jd-stat-card--metric-town-web {
	background: linear-gradient(180deg, #fffdf2 0%, #fff7d9 100%);
	border-color: #fde68a;
}

.jd-stat-top {
	display: flex;
	align-items: center;
	gap: 8px;
}

.jd-stat-chip {
	width: 24px;
	height: 24px;
	border-radius: 7px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
}

.jd-stat-chip--live {
	color: #166534;
	background: #dcfce7;
}

.jd-stat-chip--staging {
	color: #c2410c;
	background: #ffedd5;
}

.jd-stat-chip--unified {
	color: #0f766e;
	background: #ccfbf1;
}

.jd-stat-chip--town-web {
	color: #b45309;
	background: #fef3c7;
}

.jd-stat-icon {
	width: 44px;
	height: 44px;
	border-radius: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 18px;
}

.jd-stat-icon--blue   { background: #dbeafe; color: #1565C0; }
.jd-stat-icon--teal   { background: #ccfbf1; color: #0f766e; }
.jd-stat-icon--purple { background: #ede9fe; color: #6d28d9; }
.jd-stat-icon--orange { background: #fef3c7; color: #b45309; }

.jd-stat-value {
	font-size: 32px;
	font-weight: 700;
	color: #111827;
	line-height: 1;
}

.jd-stat-spin {
	font-size: 20px;
	color: #9ca3af;
}

.jd-stat-label {
	font-size: 13px;
	color: #6b7280;
	margin-top: 2px;
}

.jd-stat-label--metric {
	font-size: 12px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	margin: 0;
}

.jd-stat-value--metric {
	font-size: 34px;
}

.jd-stat-value--live {
	color: #166534;
}

.jd-stat-value--staging {
	color: #c2410c;
}

.jd-stat-value--unified {
	color: #0f766e;
}

.jd-stat-value--town-web {
	color: #b45309;
}

.jd-stat-note {
	font-size: 12px;
	color: #6b7280;
	line-height: 1.35;
}

.jd-stat-status-wrap {
	display: flex;
	align-items: center;
	gap: 14px;
}

.jd-stat-status-item {
	flex: 1;
	padding: 6px 8px;
	border-radius: 8px;
}

.jd-stat-status-value {
	font-size: 28px;
	font-weight: 700;
	line-height: 1;
	color: #111827;
}

.jd-stat-status-label {
	font-size: 12px;
	font-weight: 600;
	color: #4b5563;
	margin-top: 4px;
}

.jd-stat-status-item--live {
	background: #dcfce7;
}

.jd-stat-status-value--live,
.jd-stat-status-label--live {
	color: #166534;
}

.jd-stat-status-item--staging {
	background: #ffedd5;
}

.jd-stat-status-value--staging,
.jd-stat-status-label--staging {
	color: #c2410c;
}

.jd-stat-status-item--unified {
	background: #ccfbf1;
}

.jd-stat-status-value--unified,
.jd-stat-status-label--unified {
	color: #0f766e;
}

.jd-stat-status-item--town-web {
	background: #fef3c7;
}

.jd-stat-status-value--town-web,
.jd-stat-status-label--town-web {
	color: #b45309;
}

.jd-stat-status-divider {
	width: 1px;
	height: 38px;
	background: #e5e7eb;
}

.jd-stat-sub {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.jd-stat-pill {
	font-size: 11px;
	font-weight: 600;
	padding: 2px 8px;
	border-radius: 10px;
	display: inline-flex;
	align-items: center;
	gap: 4px;
}

.jd-stat-pill--green  { background: #dcfce7; color: #166534; }
.jd-stat-pill--indigo { background: #e0e7ff; color: #4338ca; }
.jd-stat-pill--teal   { background: #ccfbf1; color: #0f766e; }
.jd-stat-pill--blue-soft { background: #dbeafe; color: #1e40af; }
.jd-stat-pill--yellow { background: #fef3c7; color: #b45309; }

.jd-stat-link {
	font-size: 12px;
	color: #1565C0;
	text-decoration: none;
}
.jd-stat-link:hover {
	text-decoration: underline;
}

/* Section title */
.jd-section-title {
	font-size: 13px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #6b7280;
	margin-bottom: 14px;
}

.jd-bottom-grid {
	display: grid;
	grid-template-columns: 2fr 1fr;
	gap: 18px;
}

.jd-panel {
	background: #fff;
	border-radius: 12px;
	padding: 18px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
}

/* Actions grid */
.jd-actions-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 14px;
}

@media (max-width: 1100px) {
	.jd-actions-grid {
		grid-template-columns: repeat(2, 1fr);
	}
}

@media (max-width: 1200px) {
	.jd-bottom-grid {
		grid-template-columns: 1fr;
	}
}

.jd-action-card {
	background: #fcfcfd;
	border-radius: 12px;
	padding: 16px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
	text-decoration: none;
	display: flex;
	flex-direction: column;
	gap: 8px;
	transition: box-shadow 0.15s, transform 0.15s;
	border: 1px solid transparent;
}

.jd-action-card:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
	text-decoration: none;
	border-color: #e5e7eb;
}

.jd-action-icon {
	width: 40px;
	height: 40px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 16px;
	margin-bottom: 4px;
}

.jd-action-icon--blue   { background: #dbeafe; color: #1565C0; }
.jd-action-icon--teal   { background: #ccfbf1; color: #0f766e; }
.jd-action-icon--purple { background: #ede9fe; color: #6d28d9; }
.jd-action-icon--orange { background: #fef3c7; color: #b45309; }
.jd-action-icon--rose   { background: #ffe4e6; color: #be123c; }

.jd-action-label {
	font-size: 14px;
	font-weight: 600;
	color: #111827;
}

.jd-action-desc {
	font-size: 12px;
	color: #6b7280;
	line-height: 1.4;
}

.jd-latest-panel {
	display: flex;
	flex-direction: column;
}

.jd-latest-loading,
.jd-latest-empty {
	min-height: 210px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #6b7280;
	font-size: 13px;
}

.jd-latest-list {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.jd-latest-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	padding: 11px 12px;
	border-radius: 10px;
	background: #f9fafb;
	text-decoration: none;
	border: 1px solid #eef2f7;
	transition: border-color 0.15s ease, background 0.15s ease;
}

.jd-latest-client {
	display: flex;
	align-items: center;
	gap: 10px;
	min-width: 0;
}

.jd-latest-logo-wrap {
	width: 28px;
	height: 28px;
	border-radius: 50%;
	overflow: hidden;
	background: #eef2f7;
	flex-shrink: 0;
}

.jd-latest-logo {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.jd-latest-logo-fallback {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 11px;
	color: #6b7280;
}

.jd-latest-item:hover {
	background: #f3f7ff;
	border-color: #dbeafe;
}

.jd-latest-name {
	font-size: 13px;
	font-weight: 600;
	color: #111827;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.jd-latest-date {
	font-size: 12px;
	color: #6b7280;
	white-space: nowrap;
}
</style>
